import React, { useState } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { useLocation, matchPath, Route, Link, LinkProps, Switch, Redirect } from 'react-router-dom';

import { LicenseInfo } from '@mui/x-license-pro';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Drawer from '@mui/material/Drawer';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton, {
  ListItemButtonProps,
} from '@mui/material/ListItemButton';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Toolbar from '@mui/material/Toolbar';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartCheckoutOutlinedIcon from '@mui/icons-material/ShoppingCartCheckoutOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InsightsIcon from '@mui/icons-material/Insights';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';

import AppBarNav from 'AppBarNav';
import IdentityPrompt from 'shared/components/IdentityPrompt';
import NavigationGroup from 'NavigationGroup';
import Authorization from 'pages/Authorization';
import CampaignMonitor from 'pages/CampaignMonitor';
import MetaAdsInsights from 'pages/MetaAdsInsights';
import SeoGrowth from 'pages/SeoGrowth';
import Dashboard from 'pages/Dashboard';
import HomePage from 'pages/Home';
import PrivateRoute from 'pages/Authorization/PrivateRoute';

import CashflowPage from 'pages/Cashflow';
import PurchaseOrdersPage from 'pages/PurchaseOrders';
import MissingUnitsPage from 'pages/MissingUnits';
import RuleIcon from '@mui/icons-material/RuleFolder';
import SkuMasterPage from 'pages/SkuMaster';
import ShipBobPerformancePage from 'pages/ShipBobPerformance';
import OrderKittingPage from 'pages/OrderKitting';
import PostPurchasePage from 'pages/PostPurchase';
import InventoryPage from 'pages/Inventory';
import VideoCreativePage from 'pages/VideoCreative';
import StaticAdsPage from 'pages/StaticAds';
import AdLaunchPage from 'pages/AdLaunch';
import CreativePerformancePage from 'pages/CreativePerformance/CreativePerformanceTabs';
import SearchTermReview from 'pages/SearchTermReview';
import GoogleWatchdog from 'pages/GoogleWatchdog';
import ActionItemDetail from 'pages/GoogleWatchdog/DetailView';
import AuctionIntelligence from 'pages/AuctionIntelligence';
import Baselines from 'pages/Baselines';
import AgentHub from 'pages/AgentHub';
import ChangeLog from 'pages/ChangeLog';
import VideoEditingPage from 'pages/VideoEditing';
import VideoEditingPreview from 'pages/VideoEditing/PreviewPage';
import BrollLibrary from 'pages/BrollLibrary';
import MovieIcon from '@mui/icons-material/Movie';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const MUI_LICENSE_KEY = process.env.REACT_APP_MUI_LICENSE_KEY;
if (MUI_LICENSE_KEY) LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

const DRAWER_WIDTH = 240;

const navItemSx = {
  borderRadius: 2,
  mx: 1.5,
  mb: 0.25,
  py: 0.75,
  px: 1.5,
  transition: 'all 0.15s ease',
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: '#64748B',
    '& .MuiSvgIcon-root': { fontSize: 20 },
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: '#334155',
  },
  '&:hover': {
    backgroundColor: '#F1F5F9',
  },
  '&.Mui-selected': {
    // Tint plus indigo icon and bold label carry the selected state. A colored left stripe is
    // banned by DESIGN.md, and it also shifted the whole row 3px right when it appeared.
    backgroundColor: '#EEF2FF',
    '& .MuiListItemIcon-root': { color: '#4F46E5' },
    '& .MuiListItemText-primary': { color: '#4F46E5', fontWeight: 600 },
    '&:hover': { backgroundColor: '#E0E7FF' },
  },
};

const nestedNavItemSx = {
  ...navItemSx,
  ml: 3,
  mr: 1.5,
};

const CollapseListItemButton = styled(ListItemButton)<
  ListItemButtonProps & Omit<Partial<LinkProps>, 'component'> & { component?: React.ElementType }
>(({ theme }) => ({
  paddingLeft: theme.spacing(6),
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: '#334155',
  borderRadius: 8,
  marginLeft: theme.spacing(1.5),
  marginRight: theme.spacing(1.5),
  marginBottom: 2,
  transition: 'all 0.15s ease',
  '&:hover': { backgroundColor: '#F1F5F9' },
  '&.Mui-selected': {
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#E0E7FF' },
  },
}));

export default function App() {
  const location = useLocation();
  const currentPath = location.pathname;
  const inVideoEditor = currentPath.startsWith('/video-editing');
  const inOperations = ['/inventory', '/purchase-orders', '/missing-units', '/sku-master', '/cashflow', '/shipbob-performance', '/order-kitting', '/post-purchase', '/ad-launch']
    .some(path => currentPath.startsWith(path));
  const inMarketingIntelligence = [
    '/google-watchdog', '/change-log', '/search-term-review',
    '/auction-intelligence', '/meta-ads-insights', '/baselines', '/seo',
  ].some(path => currentPath.startsWith(path));
  const inCreative = currentPath.startsWith('/video-editing') || currentPath.startsWith('/video-creative') || currentPath.startsWith('/static-ads') || currentPath.startsWith('/creative-performance');
  const [expandOperations, setExpandOperations] = useState(inOperations);
  const [expandMarketingIntelligence, setExpandMarketingIntelligence] = useState(inMarketingIntelligence);
  const [expandCreative, setExpandCreative] = useState(inCreative);

  React.useEffect(() => { if (inOperations) setExpandOperations(true); }, [inOperations]);
  React.useEffect(() => {
    if (inMarketingIntelligence) setExpandMarketingIntelligence(true);
  }, [inMarketingIntelligence]);
  React.useEffect(() => { if (inCreative) setExpandCreative(true); }, [inCreative]);

  // Change Log and the explainer deck live under Google Watchdog; keep the group
  // open whenever the user is anywhere inside it (it's the only way to reach them).
  // The deck is a static file rather than a route, so it can't light up as selected.
  const inWatchdog = currentPath.startsWith('/google-watchdog') || currentPath.startsWith('/change-log');
  const [expandWatchdogMenu, setExpandWatchdogMenu] = useState(inWatchdog);
  React.useEffect(() => { if (inWatchdog) setExpandWatchdogMenu(true); }, [inWatchdog]);

  return (
    <Switch>
      {/* PUBLIC, no-login read-only preview — rendered OUTSIDE <Authorization>
          (and the app shell) so a shared link plays the edit without a tools
          login. The link's HMAC signature is the access gate, verified by the
          backend. Must stay first so it wins over the authorized routes. */}
      <Route path="/video-editing/preview/:planId">
        <VideoEditingPreview />
      </Route>
      <Route>
    <Box sx={{
      display: 'flex',
      backgroundColor: 'background.default',
      // The video editor is taller than the viewport. Let the document be its one
      // vertical scroll owner instead of nesting a scrolling main pane inside a
      // fixed-height app shell.
      height: inVideoEditor ? 'auto' : '100vh',
      minHeight: inVideoEditor ? '100vh' : undefined,
    }}>
      <GlobalStyles styles={{
        '.MuiDataGrid-root div[style*="z-index: 100000"]': { display: 'none !important' },
        '.MuiPickersPopper-root div[style*="z-index: 100000"]': { display: 'none !important' },
        '.MuiDateRangeCalendar-root div[style*="z-index"]': { display: 'none !important' },
      }} />
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Authorization>
          <React.Fragment>
            <AppBarNav />
            <IdentityPrompt />
            <Drawer
              variant="permanent"
              anchor="left"
              sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  backgroundColor: '#FFFFFF',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                },
              }}
            >
              <Toolbar />
              <List sx={{ pt: 1.5 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === '/'}
                    component={Link}
                    to="/"
                    sx={navItemSx}
                  >
                    <ListItemIcon><HomeOutlinedIcon /></ListItemIcon>
                    <ListItemText primary="Home" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath.startsWith('/agents')}
                    component={Link}
                    to="/agents"
                    sx={navItemSx}
                  >
                    <ListItemIcon><SmartToyOutlinedIcon /></ListItemIcon>
                    <ListItemText primary="Agents" />
                  </ListItemButton>
                </ListItem>

                <NavigationGroup
                  id="operations"
                  label="Operations"
                  icon={<BusinessCenterOutlinedIcon />}
                  active={inOperations}
                  expanded={expandOperations}
                  onToggle={() => setExpandOperations(expanded => !expanded)}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/inventory'}
                      component={Link}
                      to="/inventory"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><Inventory2Icon /></ListItemIcon>
                      <ListItemText primary="Inventory Forecast" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/purchase-orders'}
                      component={Link}
                      to="/purchase-orders"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><ReceiptLongIcon /></ListItemIcon>
                      <ListItemText primary="Purchase Orders" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/missing-units'}
                      component={Link}
                      to="/missing-units"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><RuleIcon /></ListItemIcon>
                      <ListItemText primary="Missing Units" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/sku-master'}
                      component={Link}
                      to="/sku-master"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><Inventory2Icon /></ListItemIcon>
                      <ListItemText primary="SKU Master" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/cashflow'}
                      component={Link}
                      to="/cashflow"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><AccountBalanceIcon /></ListItemIcon>
                      <ListItemText primary="Cashflow" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/shipbob-performance'}
                      component={Link}
                      to="/shipbob-performance"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><LocalShippingIcon /></ListItemIcon>
                      <ListItemText primary="ShipBob Performance" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/order-kitting'}
                      component={Link}
                      to="/order-kitting"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><Inventory2OutlinedIcon /></ListItemIcon>
                      <ListItemText primary="Order Kitting" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath === '/post-purchase'}
                      component={Link}
                      to="/post-purchase"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><ShoppingCartCheckoutOutlinedIcon /></ListItemIcon>
                      <ListItemText primary="Post-Purchase Upsells" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/ad-launch')}
                      component={Link}
                      to="/ad-launch"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><RocketLaunchOutlinedIcon /></ListItemIcon>
                      <ListItemText primary="Ad Launcher" />
                    </ListItemButton>
                  </ListItem>
                </NavigationGroup>

                <NavigationGroup
                  id="marketing-intelligence"
                  label="Marketing Intelligence"
                  icon={<AnalyticsOutlinedIcon />}
                  active={inMarketingIntelligence}
                  expanded={expandMarketingIntelligence}
                  onToggle={() => setExpandMarketingIntelligence(expanded => !expanded)}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/google-watchdog')}
                      component={Link}
                      to="/google-watchdog"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><FactCheckIcon /></ListItemIcon>
                      <ListItemText primary="Google Watchdog" />
                      {expandWatchdogMenu ? (
                        <ExpandLess
                          sx={{ color: '#64748B' }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); setExpandWatchdogMenu(false); }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{ color: '#64748B' }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); setExpandWatchdogMenu(true); }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={expandWatchdogMenu} timeout="auto" unmountOnExit>
                    <CollapseListItemButton
                      selected={currentPath.startsWith('/change-log')}
                      component={Link}
                      to="/change-log"
                      sx={{ pl: 9 }}
                    >
                      Change Log
                    </CollapseListItemButton>
                    <CollapseListItemButton
                      component="a"
                      href="/watchdog-deck.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ pl: 9 }}
                    >
                      How It Works
                      <OpenInNewIcon sx={{ fontSize: 14, ml: 0.75, color: '#94A3B8' }} />
                    </CollapseListItemButton>
                  </Collapse>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/search-term-review')}
                      component={Link}
                      to="/search-term-review"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><ManageSearchIcon /></ListItemIcon>
                      <ListItemText primary="Search Term Review" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/auction-intelligence')}
                      component={Link}
                      to="/auction-intelligence"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><QueryStatsIcon /></ListItemIcon>
                      <ListItemText primary="Auction Intelligence" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/baselines')}
                      component={Link}
                      to="/baselines"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><MonitorHeartOutlinedIcon /></ListItemIcon>
                      <ListItemText primary="Metric Baselines" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/meta-ads-insights')}
                      component={Link}
                      to="/meta-ads-insights"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><InsightsIcon /></ListItemIcon>
                      <ListItemText primary="Meta Ads Insights" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/seo')}
                      component={Link}
                      to="/seo"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><TravelExploreIcon /></ListItemIcon>
                      <ListItemText primary="SEO Decision Log" />
                    </ListItemButton>
                  </ListItem>
                </NavigationGroup>

                <NavigationGroup
                  id="creative"
                  label="Creative"
                  icon={<AutoAwesomeOutlinedIcon />}
                  active={inCreative}
                  expanded={expandCreative}
                  onToggle={() => setExpandCreative(expanded => !expanded)}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/video-editing')}
                      component={Link}
                      to="/video-editing"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><MovieIcon /></ListItemIcon>
                      <ListItemText primary="Video Editing" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/broll-library')}
                      component={Link}
                      to="/broll-library"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><VideoLibraryIcon /></ListItemIcon>
                      <ListItemText primary="B-Roll Library" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/video-creative')}
                      component={Link}
                      to="/video-creative"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><LibraryBooksIcon /></ListItemIcon>
                      <ListItemText primary="Video Creative" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/static-ads')}
                      component={Link}
                      to="/static-ads"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><ImageOutlinedIcon /></ListItemIcon>
                      <ListItemText primary="Static Creative" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={currentPath.startsWith('/creative-performance')}
                      component={Link}
                      to="/creative-performance"
                      sx={nestedNavItemSx}
                    >
                      <ListItemIcon><InsightsIcon /></ListItemIcon>
                      <ListItemText primary="Creative Performance" />
                    </ListItemButton>
                  </ListItem>
                </NavigationGroup>

                {/* Hidden tabs - Campaign Monitor, Dashboard
                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === 'campaign-monitor'}
                    component={Link}
                    to="/campaign-monitor"
                    sx={navItemSx}
                  >
                    <ListItemIcon>
                      <MonitorHeartIcon />
                    </ListItemIcon>
                    <ListItemText primary="Campaign Monitor" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    selected={currentPath === '/dashboard'}
                    component={Link}
                    to="/dashboard"
                    sx={navItemSx}
                  >
                    <ListItemIcon>
                      <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                  </ListItemButton>
                </ListItem>
                */}

              </List>
            </Drawer>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: 4,
                minWidth: 0,
                // The Watchdog list is designed on white, with Urgent as its only tinted block.
                bgcolor: matchPath(currentPath, { path: ['/google-watchdog', '/google-watchdog/triage'], exact: true }) ? '#FFFFFF' : undefined,
                maxWidth: '100%',
                // `hidden` makes the other overflow axis compute to `auto`, which
                // created a second vertical scrollbar on this long page. `clip`
                // still contains the wide timeline without becoming a scroller.
                overflowX: inVideoEditor ? 'clip' : 'hidden',
              }}
            >
              <Toolbar />
              <Switch>
                <PrivateRoute exact path="/" allowedTypes={['admin']}>
                  <HomePage />
                </PrivateRoute>

                <PrivateRoute path="/campaign-monitor" allowedTypes={['admin']}>
                  <CampaignMonitor />
                </PrivateRoute>

                <PrivateRoute
                  path="/meta-ads-insights"
                  allowedTypes={['admin']}
                >
                  <MetaAdsInsights />
                </PrivateRoute>

                <PrivateRoute path="/seo" allowedTypes={['admin']}>
                  <SeoGrowth />
                </PrivateRoute>

                <PrivateRoute path="/dashboard" allowedTypes={['admin']}>
                  <Dashboard />
                </PrivateRoute>

                <PrivateRoute path="/inventory" allowedTypes={['admin']}>
                  <InventoryPage />
                </PrivateRoute>

                <PrivateRoute path="/purchase-orders" allowedTypes={['admin']}>
                  <PurchaseOrdersPage />
                </PrivateRoute>

                <PrivateRoute path="/missing-units" allowedTypes={['admin']}>
                  <MissingUnitsPage />
                </PrivateRoute>

                <PrivateRoute path="/sku-master" allowedTypes={['admin']}>
                  <SkuMasterPage />
                </PrivateRoute>

                <PrivateRoute path="/cashflow" allowedTypes={['admin']}>
                  <CashflowPage />
                </PrivateRoute>

                <PrivateRoute
                  path="/shipbob-performance"
                  allowedTypes={['admin']}
                >
                  <ShipBobPerformancePage />
                </PrivateRoute>

                <PrivateRoute path="/order-kitting" allowedTypes={['admin']}>
                  <OrderKittingPage />
                </PrivateRoute>

                <PrivateRoute path="/post-purchase" allowedTypes={['admin']}>
                  <PostPurchasePage />
                </PrivateRoute>

                {/* Attribution was retired 2026-10-23 (misleading: pre-kitting-fix TW revenue, UTC days, its own MER/True ROAS) */}
                <Redirect from="/attribution" to="/baselines" />

                <PrivateRoute path="/video-creative" allowedTypes={['admin']}>
                  <VideoCreativePage />
                </PrivateRoute>

                <PrivateRoute path="/static-ads" allowedTypes={['admin']}>
                  <StaticAdsPage />
                </PrivateRoute>

                <PrivateRoute path="/ad-launch" allowedTypes={['admin']}>
                  <AdLaunchPage />
                </PrivateRoute>

                <PrivateRoute path="/creative-performance" allowedTypes={['admin']}>
                  <CreativePerformancePage />
                </PrivateRoute>

                <PrivateRoute path="/search-term-review" allowedTypes={['admin']}>
                  <SearchTermReview />
                </PrivateRoute>

                <PrivateRoute path="/auction-intelligence" allowedTypes={['admin']}>
                  <AuctionIntelligence />
                </PrivateRoute>

                <PrivateRoute path={['/agents/:name', '/agents']} allowedTypes={['admin']}>
                  <AgentHub />
                </PrivateRoute>

                <PrivateRoute path="/baselines" allowedTypes={['admin']}>
                  <Baselines />
                </PrivateRoute>

                <PrivateRoute
                  path="/google-watchdog/items/:id"
                  allowedTypes={['admin']}
                >
                  <ActionItemDetail />
                </PrivateRoute>

                {/* The triage route's card: the same page plus the agent's action steps. */}
                <PrivateRoute path="/google-watchdog/triage/items/:id" allowedTypes={['admin']}>
                  <ActionItemDetail actionSteps />
                </PrivateRoute>

                <PrivateRoute exact path="/google-watchdog" allowedTypes={['admin']}>
                  <GoogleWatchdog />
                </PrivateRoute>

                {/* The same list with campaign numbers: the performance table and per-card numbers. */}
                <PrivateRoute exact path="/google-watchdog/triage" allowedTypes={['admin']}>
                  <GoogleWatchdog campaignNumbers />
                </PrivateRoute>

                <Redirect from="/ad-library/:tab" to="/video-creative/:tab" />
                <Redirect from="/ad-library" to="/video-creative" />
                <Redirect exact from="/action-center" to="/google-watchdog" />
                <Redirect from="/action-center/items/:id" to="/google-watchdog/items/:id" />

                <PrivateRoute exact path="/change-log" allowedTypes={['admin']}>
                  <ChangeLog />
                </PrivateRoute>

                <PrivateRoute path="/video-editing" allowedTypes={['admin']}>
                  <VideoEditingPage />
                </PrivateRoute>

                <PrivateRoute path="/broll-library" allowedTypes={['admin']}>
                  <BrollLibrary />
                </PrivateRoute>


              </Switch>
            </Box>
          </React.Fragment>
        </Authorization>
      </MuiPickersUtilsProvider>
    </Box>
      </Route>
    </Switch>
  );
}
